'use client';

import React, { useState, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import Button from '@/components/button/button';
import ReCAPTCHA from 'react-google-recaptcha';

export default function AuthorizedAgentRequestForm() {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [form, setForm] = useState({
    yourFirstName: '',
    yourLastName: '',
    yourEmail: '',
    agentFirstName: '',
    agentLastName: '',
    agentEmail: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<string | null>(null);
  const [errorResult, setErrorResult] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);
    setErrorResult(null);
    setShowCaptchaError(false);

    if (!captchaValue) {
      setShowCaptchaError(true);
      setIsSubmitting(false);
      return;
    }

    // Compose Zendesk JSON
    const description = `Hello, the following Authorized Agent Request has been submitted. This request should be handled in consideration of CCPA compliance.\n\nName: ${form.yourFirstName} ${form.yourLastName}\nEmail: ${form.yourEmail}\nAgent Name: ${form.agentFirstName} ${form.agentLastName}\nAgent Email: ${form.agentEmail}`;
    const payload = {
      ticket: {
        requester: {
          local_id: 1,
          name: `${form.yourFirstName} ${form.yourLastName}`,
          email: form.yourEmail,
        },
        priority: 'high',
        tags: ['authorized_agent_request'],
        subject: 'Authorized Agent Request',
        description,
      },
    };

    try {
      const res = await fetch('/api/zendesk-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setSubmitResult('Request submitted successfully!');
      setForm({
        yourFirstName: '',
        yourLastName: '',
        yourEmail: '',
        agentFirstName: '',
        agentLastName: '',
        agentEmail: '',
      });
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch {
      setErrorResult('There was an error submitting the request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Your First Name</label>
            <input
              type="text"
              name="yourFirstName"
              value={form.yourFirstName}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Agent First Name</label>
            <input
              type="text"
              name="agentFirstName"
              value={form.agentFirstName}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Your Last Name</label>
            <input
              type="text"
              name="yourLastName"
              value={form.yourLastName}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Agent Last Name</label>
            <input
              type="text"
              name="agentLastName"
              value={form.agentLastName}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Your Email</label>
            <input
              type="email"
              name="yourEmail"
              value={form.yourEmail}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={6} className="form-group">
          <div className="form-item">
            <label>Agent Email</label>
            <input
              type="email"
              name="agentEmail"
              value={form.agentEmail}
              onChange={handleChange}
              required
            />
          </div>
        </Col>
        <Col md={12} className="form-group">
          <div className="form-item">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={'6LdBcCEUAAAAABcH2WFGbc2ZfOyt60UapRX5EXOM'}
              onChange={(value) => {
                setCaptchaValue(value);
                setShowCaptchaError(false);
              }}
            />
            {showCaptchaError && (
              <div className="error-message">Please complete the reCAPTCHA</div>
            )}
          </div>
        </Col>
        <div className="btn-wrap">
          <Button type="submit" variant="secondary" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
        {submitResult && (
          <div className="mt-3 alert alert-success">{submitResult}</div>
        )}
        {errorResult && (
          <div className="mt-3 alert alert-danger">{errorResult}</div>
        )}
      </Row>
    </Form>
  );
}
